import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheckIcon,
  SunIcon,
  MoonIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  BeakerIcon,
  DocumentMagnifyingGlassIcon,
  BookOpenIcon,
  RocketLaunchIcon,
  Squares2X2Icon,
  ArrowDownTrayIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';

import apiClient from '../services/api';
import BatchProcessingGuide from './BatchProcessingGuide';
import SingleRequirementGuide from './SingleRequirementGuide';

function AppHeader({ showInstructions, setShowInstructions, userId, handleLogout }) {
  const [isDark, setIsDark] = useState(false);
  const feedbackFormUrl =
    process.env.REACT_APP_FEEDBACK_FORM_URL ||
    'https://forms.office.com/Pages/ResponsePage.aspx?id=zdVS2ExyKEGIEv-l2z-FB8xhL24jh-RCkkXq9jFt9lxUN09HWlBOSFk0NDhYVzFYSzNNRkhOSjBPVCQlQCN0PWcu';

  // Check for saved theme preference or default to light mode
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
    setIsDark(!isDark);
  };

  const guidePages = [
    { key: 'overview', label: 'Overview', icon: BookOpenIcon },
    { key: 'batch-processing', label: 'Batch Processing', icon: Squares2X2Icon },
    { key: 'single-requirement-analysis', label: 'Single Requirement', icon: BeakerIcon }
  ];

  return (
    <>
      {/* Main Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-0 z-40 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-lg border-b border-neutral-200 dark:border-neutral-800"
      >
        <div className="w-full px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Logo and brand */}
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="p-2 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg shadow-lg shadow-primary-500/25"
              >
                <ShieldCheckIcon className="w-6 h-6 text-white" />
              </motion.div>
              <div>
                <h1 className="text-xl font-bold text-neutral-900 dark:text-white">
                  AI INCOSE Recommendation [Requirements]
                </h1>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Supported Formats: PLM, FuSa, Medini (.xlsx, .docx)
                </p>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-2">
              {/* Feedback button */}
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={feedbackFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400 transition-colors"
                title="Open feedback form"
              >
                <ChatBubbleLeftEllipsisIcon className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:inline">Feedback</span>
              </motion.a>

              {/* Release Notes button */}
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="https://eng-stla.atlassian.net/wiki/spaces/~63198f6a62fe1e6eac6df534/pages/2140538587/AI+-+MBSE+SSD+Project"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400 transition-colors"
                title="View release notes"
              >
                <DocumentMagnifyingGlassIcon className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:inline">Release Notes</span>
              </motion.a>

              {/* Guide dropdown */}
              <Menu as="div" className="relative">
                <Menu.Button
                  as={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                    showInstructions
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                  }`}
                >
                  <span className="text-sm font-medium">Guide</span>
                  <ChevronDownIcon className="w-4 h-4" />
                </Menu.Button>

                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-white dark:bg-neutral-900 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="p-1">
                      {guidePages.map((page) => (
                        <Menu.Item key={page.key}>
                          {({ active }) => (
                            <button
                              onClick={() =>
                                setShowInstructions(showInstructions === page.key ? null : page.key)
                              }
                              className={`${
                                active ? 'bg-neutral-100 dark:bg-neutral-800' : ''
                              } ${
                                showInstructions === page.key ? 'text-primary-600 dark:text-primary-400' : 'text-neutral-700 dark:text-neutral-300'
                              } group flex items-center gap-3 px-3 py-2 text-sm rounded-lg w-full transition-colors`}
                            >
                              <page.icon className="w-5 h-5" />
                              {page.label}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* Dark mode toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleDarkMode}
                className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400 transition-colors"
                title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              >
                <AnimatePresence mode="wait">
                  {isDark ? (
                    <motion.div
                      key="moon"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <MoonIcon className="w-5 h-5" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="sun"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <SunIcon className="w-5 h-5" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>

              {/* User menu */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                  <UserCircleIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {userId || 'User'}
                  </span>
                  <ChevronDownIcon className="w-4 h-4 text-neutral-400" />
                </Menu.Button>

                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white dark:bg-neutral-900 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="p-1">
                      <Menu.Item>
                        {({ active }) => (
                          <div className={`${
                            active ? 'bg-neutral-100 dark:bg-neutral-800' : ''
                          } px-3 py-2 rounded-lg cursor-default`}>
                            <div className="text-xs text-neutral-500 dark:text-neutral-400">Logged in as</div>
                            <div className="text-sm font-medium text-neutral-900 dark:text-white">{userId}</div>
                          </div>
                        )}
                      </Menu.Item>
                      <div className="h-px bg-neutral-200 dark:bg-neutral-700 my-1" />
                      {handleLogout && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`${
                                active ? 'bg-danger-50 dark:bg-danger-900/20' : ''
                              } group flex items-center gap-2 px-3 py-2 text-sm text-danger-600 dark:text-danger-400 rounded-lg w-full transition-colors`}
                            >
                              <ArrowRightOnRectangleIcon className="w-4 h-4" />
                              Sign out
                            </button>
                          )}
                        </Menu.Item>
                      )}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Guide Panel */}
      <AnimatePresence>
        {showInstructions && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="relative bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-50 dark:from-primary-950 dark:via-secondary-950 dark:to-primary-950 border-b border-primary-200 dark:border-primary-800 overflow-hidden">
              {/* Background decorations */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -top-20 -left-20 w-40 h-40 bg-primary-400 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-secondary-400 rounded-full blur-3xl"></div>
              </div>

              <div className="relative w-full px-6 py-8">
                {/* Header with page title and close button */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl">
                      {showInstructions === 'overview' && <BookOpenIcon className="w-6 h-6 text-white" />}
                      {showInstructions === 'batch-processing' && <RocketLaunchIcon className="w-6 h-6 text-white" />}
                      {showInstructions === 'single-requirement-analysis' && <BeakerIcon className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-neutral-900 dark:text-white">
                        {showInstructions === 'overview' && 'Overview'}
                        {showInstructions === 'batch-processing' && 'Batch Processing Guide'}
                        {showInstructions === 'single-requirement-analysis' && 'Single Requirement Analysis Guide'}
                      </h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {showInstructions === 'overview' && 'Learn about AI INCOSE Recommendation [Requirements]'}
                        {showInstructions === 'batch-processing' && 'Get started in 6 simple steps'}
                        {showInstructions === 'single-requirement-analysis' && 'Analyze and improve individual requirements'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowInstructions(null)}
                    className="p-2 hover:bg-white/50 dark:hover:bg-neutral-800/50 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-neutral-600 dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Overview Page */}
                {showInstructions === 'overview' && (
                  <div className="space-y-4">
                    <div className="bg-white dark:bg-neutral-900 rounded-xl p-6 shadow-lg">
                      <h4 className="text-lg font-bold text-neutral-900 dark:text-white mb-3">What is AI INCOSE Recommendation [Requirements]?</h4>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
                        AI INCOSE Recommendation [Requirements] is a platform that allows teams to review and improve their requirement documents by automatically checking them against quality standards (a set of rules). 
                        Users can simply upload their requirements and (optionally) a rule set; the tool uses artificial intelligence to analyze each requirement, comparing it against a checklist of rules and guidelines derived from INCOSE standards.
                        The system then generates clear, actionable reports that highlight areas needing improvement, ensuring requirements are complete, well-structured, and meet organizational standards.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                          <h5 className="text-sm font-bold text-primary-700 dark:text-primary-300 mb-1">Validate</h5>
                          <p className="text-xs text-neutral-600 dark:text-neutral-400">Check requirements against defined quality rules</p>
                        </div>
                        <div className="p-4 bg-secondary-50 dark:bg-secondary-900/20 rounded-lg">
                          <h5 className="text-sm font-bold text-secondary-700 dark:text-secondary-300 mb-1">Analyze</h5>
                          <p className="text-xs text-neutral-600 dark:text-neutral-400">Get detailed reports with actionable feedback</p>
                        </div>
                        <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                          <h5 className="text-sm font-bold text-primary-700 dark:text-primary-300 mb-1">Export</h5>
                          <p className="text-xs text-neutral-600 dark:text-neutral-400">Download results for further review and sharing</p>
                        </div>
                      </div>
                    </div>

                    {/* Download User Guide PDF */}
                    <div className="flex justify-center">
                      <button
                        onClick={async () => {
                          try {
                            const response = await apiClient.get('/guide/download', {
                              responseType: 'blob',
                            });
                            const blob = response.data;
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = 'User_Guide.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                          } catch (err) {
                            alert('Failed to download user guide: ' + err.message);
                          }
                        }}
                        className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all"
                      >
                        <ArrowDownTrayIcon className="w-5 h-5" />
                        Download Full User Guide (PDF)
                      </button>
                    </div>
                  </div>
                )}

                {/* Batch Processing Guide Page */}
                {showInstructions === 'batch-processing' && <BatchProcessingGuide />}

                {/* Single Requirement Analysis Guide Page */}
                {showInstructions === 'single-requirement-analysis' && <SingleRequirementGuide />}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default AppHeader;